import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Drawer,
    IconButton,
    Grid,
    Button,
    Divider,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { isEqual, cloneDeep } from 'lodash';
import {
    RESET_SETUP,
    createSetup,
    editSetup,
} from '../../store/setup';
import {
    AdminSidePanelAction, ScheduleSource, AnchorDay,
} from '../../../Enums';
import InformationSection from './InformationSection';
import AccessSection from './AccessSection';
import DataviewSection from './DataviewSection';
import FunctionSection from './FunctionSection';
import RuleSection from './RuleSection';
import NotificationSection from './NotificationSection';
import './SetupEdit.scss';

/**
 * @param {{ open: boolean; setOpen: Function; }} props
 */
function SetupEdit({ open, setOpen }) {
    const dispatch = useDispatch();
    /** @type {ShiftBidding.AdminStore} */
    // @ts-ignore
    const { adminSetup, adminAppData, pendingTasks } = useSelector((state) => state);
    const { adminSidePanelAction, isUsed } = adminSetup;
    const { setups, hyperfinds } = adminAppData;

    const informationSectionRef = useRef(null);
    const accessSectionRef = useRef(null);
    const dataviewSectionRef = useRef(null);
    const functionSectionRef = useRef(null);
    const bidRulesSectionRef = useRef(null);

    const disabledSections = adminSidePanelAction === AdminSidePanelAction.Edit && isUsed ? {
        creationHyperfindId: true,
        managementHyperfindId: true,
        viewHyperfindId: true,
        bidProcessType: true,
        scheduleSource: true,
        anchorDay: true,
        qualificationRule: true,
        awardingRuleSetId: true,
        employeeMustSubmitRequiredAmountOfBids: true,
        isRebidAllowed: true,
        isAllowEmployeesToLockForRebid: true,
        isAutoApproval: true,
        isAutoSignOff: true,
        isOverrideSchedule: true,
        isEmployeeCanDeclineBid: true,
        isBidOnBehalfAllowedWhenActive: true,
        isBidOnBehalfAllowedWhenClosed: true,
        isEditBidOnBehalfAllowedWhenActive: true,
        isEditBidOnBehalfAllowedWhenClosed: true,
        isEditEmployeesWhenBidActive: true,
        isGroupShiftlinesWithSameSchedules: true,
        isAllowBidOnActiveWorkplan: true,
        dataviewId: false,
        dataviewDisplayedCols: false,
        weeklyHours: true,
        isEnableEmployeeRankReport: true,
        isEnableEmployeeBidResultReport: true,
        isEmployeeCanBidAfterAllottedTime: true,
        sequentialBidClosingDelay: true,
    } : {
        creationHyperfindId: false,
        managementHyperfindId: false,
        viewHyperfindId: false,
        bidProcessType: false,
        scheduleSource: false,
        anchorDay: false,
        qualificationRule: false,
        awardingRuleSetId: false,
        employeeMustSubmitRequiredAmountOfBids: false,
        isRebidAllowed: false,
        isAllowEmployeesToLockForRebid: false,
        isAutoApproval: false,
        isAutoSignOff: false,
        isOverrideSchedule: false,
        isEmployeeCanDeclineBid: false,
        isBidOnBehalfAllowedWhenActive: false,
        isBidOnBehalfAllowedWhenClosed: false,
        isEditBidOnBehalfAllowedWhenActive: false,
        isEditBidOnBehalfAllowedWhenClosed: false,
        isEditEmployeesWhenBidActive: false,
        isGroupShiftlinesWithSameSchedules: false,
        isAllowBidOnActiveWorkplan: false,
        dataviewId: false,
        dataviewDisplayedCols: false,
        weeklyHours: false,
        isEnableEmployeeRankReport: false,
        isEnableEmployeeBidResultReport: false,
        isEmployeeCanBidAfterAllottedTime: false,
        sequentialBidClosingDelay: false
    };

    const [validationErrors, setValidationErrors] = useState({
        name: false,
        creationHyperfindId: false,
        managementHyperfindId: false,
        viewHyperfindId: false,
        dataviewId: false,
        dataviewDisplayedCols: false,
        bidProcessType: false,
        scheduleSource: false,
        anchorDay: false,
        qualificationRule: false,
        awardingRuleSetId: false,
        nameErrorMessage: '',
    });

    const validate = () => {
        let hasError = false;
        let errorRef;
        const errors = {
            name: false,
            creationHyperfindId: false,
            managementHyperfindId: false,
            viewHyperfindId: false,
            dataviewId: false,
            dataviewDisplayedCols: false,
            bidProcessType: false,
            scheduleSource: false,
            anchorDay: false,
            qualificationRule: false,
            awardingRuleSetId: false,
            nameErrorMessage: '',
        };
        const {
            bidSetupId,
            name,
            creationHyperfindId,
            managementHyperfindId,
            viewHyperfindId,
            dataviewId,
            dataviewDisplayedCols,
            scheduleSource,
            anchorDay,
            awardingRuleSetId,
        } = adminSetup;

        // Information section
        if (!name.trim()) {
            hasError = true;
            errors.name = true;
            errorRef = informationSectionRef;
            errors.nameErrorMessage = 'Name cannot be empty';
        } else {
            const value = setups.find((x) => x.name === name && x.bidSetupId !== bidSetupId);
            if (value) {
                hasError = true;
                errors.name = true;
                errorRef = informationSectionRef;
                errors.nameErrorMessage = 'Name is already in use. Please select a unique name';
            }
        }

        // Display access section
        if (!hyperfinds.find((x) => x.value === creationHyperfindId)) {
            hasError = true;
            errors.creationHyperfindId = true;
            errorRef = accessSectionRef;
        }

        if (!hyperfinds.find((x) => x.value === managementHyperfindId)) {
            hasError = true;
            errors.managementHyperfindId = true;
            errorRef = accessSectionRef;
        }

        if (!hyperfinds.find((x) => x.value === viewHyperfindId)) {
            hasError = true;
            errors.viewHyperfindId = true;
            errorRef = accessSectionRef;
        }

        // Dataview section
        if (dataviewId === undefined) {
            hasError = true;
            errors.dataviewId = true;
            errorRef = dataviewSectionRef;
        } else if (!dataviewDisplayedCols.length) {
            hasError = true;
            errors.dataviewDisplayedCols = true;
            errorRef = dataviewSectionRef;
        }

        // Function section
        if (scheduleSource !== ScheduleSource.ExcelFile && scheduleSource !== ScheduleSource.WorkplanManager) {
            hasError = true;
            errors.scheduleSource = true;
            errorRef = functionSectionRef;
        }
        if (scheduleSource === ScheduleSource.ExcelFile) {
            if (anchorDay == null) {
                hasError = true;
                errors.anchorDay = true;
                errorRef = functionSectionRef;
            } else if (anchorDay !== null && !Object.values(AnchorDay).includes(anchorDay)) {
                hasError = true;
                errors.anchorDay = true;
                errorRef = functionSectionRef;
            }
        }

        // Bid rules section
        if (awardingRuleSetId === -1) {
            hasError = true;
            errors.awardingRuleSetId = true;
            errorRef = bidRulesSectionRef;
        }

        if (errors.name !== validationErrors.name || errors.creationHyperfindId !== validationErrors.creationHyperfindId
            || errors.managementHyperfindId !== validationErrors.managementHyperfindId
            || errors.viewHyperfindId !== validationErrors.viewHyperfindId
            || errors.dataviewDisplayedCols !== validationErrors.dataviewDisplayedCols
            || errors.bidProcessType !== validationErrors.bidProcessType || errors.scheduleSource !== validationErrors.scheduleSource
            || errors.anchorDay !== validationErrors.anchorDay
            || errors.qualificationRule !== validationErrors.qualificationRule
            || errors.awardingRuleSetId !== validationErrors.awardingRuleSetId
            || errors.nameErrorMessage !== validationErrors.nameErrorMessage) {
            setValidationErrors(errors);
        }
        if (hasError && errorRef && errorRef.current) {
            errorRef.current.scrollIntoView();
        }

        return !hasError;
    };

    const closeSidePanel = () => {
        setValidationErrors({
            name: false,
            creationHyperfindId: false,
            managementHyperfindId: false,
            viewHyperfindId: false,
            dataviewId: false,
            dataviewDisplayedCols: false,
            bidProcessType: false,
            scheduleSource: false,
            anchorDay: false,
            qualificationRule: false,
            awardingRuleSetId: false,
            nameErrorMessage: '',
        });
        dispatch({ type: RESET_SETUP });
        setOpen(false);
    };

    const handleSubmitSetup = () => {
        if (validate()) {
            switch (adminSidePanelAction) {
                case AdminSidePanelAction.Create:
                    dispatch(createSetup({ ...adminSetup, bidSetupId: '' }, { hyperfinds }, () => setOpen(false), false));
                    break;
                case AdminSidePanelAction.Duplicate:
                    dispatch(createSetup({ ...adminSetup, bidSetupId: '' }, { hyperfinds }, () => setOpen(false), true));
                    break;
                case AdminSidePanelAction.Edit:
                    dispatch(editSetup(adminSetup, { setups, hyperfinds }, () => setOpen(false)));
                    break;
                default:
                    break;
            }
        }
    };
    const [originalBidSetup, setOriginalBidSetup] = useState(cloneDeep(adminSetup));
    useEffect(() => setOriginalBidSetup(cloneDeep(adminSetup)), [adminSetup.bidSetupId]);
    const isDirty = isEqual(originalBidSetup, adminSetup) && adminSetup.adminSidePanelAction !== 3;
    let stepIndex = 1;

    return (
        <Drawer
            anchor="right"
            open={open}
            classes={{ paper: 'setupEditDrawerPaper' }}
        >
            <Grid
                container
                justifyContent="space-between"
                className="sue-header-section"
            >
                <Grid
                    item
                    className="title-container"
                >
                    <h3 className="title-label">
                        Configuration Set
                    </h3>
                </Grid>
                <Grid item>
                    <IconButton
                        className="drawer-x-icon-button"
                        onClick={() => closeSidePanel()}
                    >
                        <FontAwesomeIcon
                            icon={faTimes}
                            size="sm"
                        />
                    </IconButton>
                </Grid>
            </Grid>
            <Grid
                container
                className="sue-content-section"
            >
                <InformationSection
                    stepIndex={stepIndex++}
                    errorRef={informationSectionRef}
                    validationErrors={validationErrors}
                />
                <AccessSection
                    stepIndex={stepIndex++}
                    errorRef={accessSectionRef}
                    validationErrors={validationErrors}
                    disabledSections={disabledSections}
                />
                <DataviewSection
                    stepIndex={stepIndex++}
                    errorRef={dataviewSectionRef}
                    validationErrors={validationErrors}
                    disabledSections={disabledSections}
                />
                <FunctionSection
                    stepIndex={stepIndex++}
                    errorRef={functionSectionRef}
                    validationErrors={validationErrors}
                    disabledSections={disabledSections}
                />
                <RuleSection
                    stepIndex={stepIndex++}
                    errorRef={bidRulesSectionRef}
                    validationErrors={validationErrors}
                    disabledSections={disabledSections}
                />
                <NotificationSection stepIndex={stepIndex++} />
            </Grid>
            <Divider />
            <Grid
                container
                justifyContent="flex-end"
            >
                <Button
                    variant="contained"
                    onClick={handleSubmitSetup}
                    disabled={isDirty || !!pendingTasks}
                >
                    Save
                </Button>
                <Button
                    variant="contained"
                    onClick={closeSidePanel}
                >
                    Cancel
                </Button>
            </Grid>
        </Drawer>
    );
}

export default SetupEdit;
